import React, { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Mail,
  Calendar,
  RefreshCw,
  X as XIcon,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Send,
} from 'lucide-react';
import { useSentInvitationsQuery } from './useSentInvitationsQuery';
import { formatInvitationError } from '@/lib/reviewInvitationErrors';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { type SentInvitation } from '@/lib/api/reviews/sent-invitations';

const RESEND_COOLDOWN_HOURS = 24;
const MAX_RESENDS = 2;

function statusBadge(status: string) {
  const map: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
    pending: { label: 'Pending', variant: 'secondary' },
    accepted: { label: 'Accepted', variant: 'default' },
    declined: { label: 'Declined', variant: 'outline' },
    expired: { label: 'Expired', variant: 'outline' },
    revoked: { label: 'Revoked', variant: 'outline' },
    bounced: { label: 'Delivery failed', variant: 'destructive' },
    completed: { label: 'Completed', variant: 'default' },
  };
  const meta = map[status] ?? { label: status, variant: 'outline' as const };
  return (
    <Badge variant={meta.variant} className="text-[10px] uppercase tracking-wide">
      {meta.label}
    </Badge>
  );
}

function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// A previous attempt is considered failed when the email never reached the
// recipient — either the provider hard-bounced (status = 'bounced') or our
// send call returned an error (last_error set with no delivered_at).
function previousAttemptFailed(inv: SentInvitation): boolean {
  if (inv.status === 'bounced') return true;
  return !inv.delivered_at && !!inv.last_error;
}

function canResend(inv: SentInvitation): boolean {
  // Allow retry on pending and bounced (server will re-open bounced rows).
  if (inv.status !== 'pending' && inv.status !== 'bounced') return false;
  // Failure recovery bypasses cooldown and the resend cap, mirroring the
  // edge function's logic in handleResend.
  if (previousAttemptFailed(inv)) return true;
  if ((inv.resend_count ?? 0) >= MAX_RESENDS) return false;
  const last = inv.last_sent_at ? new Date(inv.last_sent_at).getTime() : 0;
  return Date.now() - last >= RESEND_COOLDOWN_HOURS * 3600 * 1000;
}

const SentInvitationsList: React.FC = () => {
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useSentInvitationsQuery();

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['sent-invitations'] });
    refetch();
  }, [queryClient, refetch]);

  const handleResend = useCallback(
    async (id: string) => {
      const { error: err } = await supabase.functions.invoke('manage-review-invitation', {
        body: { action: 'resend', invitationId: id },
      });
      if (err) {
        toast.error(err.message ?? 'Resend failed');
        return;
      }
      toast.success('Invitation re-sent');
      refresh();
    },
    [refresh],
  );

  const handleRevoke = useCallback(
    async (id: string) => {
      const { error: err } = await supabase.functions.invoke('manage-review-invitation', {
        body: { action: 'revoke', invitationId: id },
      });
      if (err) {
        toast.error(err.message ?? 'Revoke failed');
        return;
      }
      toast.success('Invitation revoked');
      refresh();
    },
    [refresh],
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-5 bg-muted rounded w-2/3" />
            </CardHeader>
            <CardContent>
              <div className="h-4 bg-muted rounded w-1/2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-destructive py-4">
        Could not load sent invitations. Please try again.
      </p>
    );
  }

  const invitations = data?.invitations ?? [];

  return (
    <div className="space-y-4">

      {invitations.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Send className="h-12 w-12 text-muted-foreground mb-4" aria-hidden />
            <h3 className="text-lg font-semibold mb-2">No invitations sent yet</h3>
            <p className="text-muted-foreground text-center max-w-md">
              Open one of your papers and use "Request Review" to invite peers by email.
            </p>
          </CardContent>
        </Card>
      ) : (
        invitations.map((inv) => (
          <Card key={inv.id}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <CardTitle className="text-base mb-1 truncate">
                    {inv.paper_title ? (
                      <Link
                        to={`/paper/${inv.paper_id}`}
                        className="hover:underline"
                        title={inv.paper_title}
                      >
                        {inv.paper_title}
                      </Link>
                    ) : (
                      'Untitled paper'
                    )}
                  </CardTitle>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Mail className="h-3 w-3" aria-hidden />
                      <span className="truncate max-w-[260px]" title={inv.invitee_email ?? ''}>
                        {inv.invitee_email ?? '—'}
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" aria-hidden />
                      Sent {formatDate(inv.sent_at ?? inv.created_at)}
                    </span>
                    {inv.delivered_at && (
                      <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400">
                        <CheckCircle2 className="h-3 w-3" aria-hidden />
                        Delivered
                      </span>
                    )}
                    {inv.status === 'pending' && inv.expires_at && (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" aria-hidden />
                        Expires {formatDate(inv.expires_at)}
                      </span>
                    )}
                  </div>
                </div>
                {statusBadge(inv.status)}
              </div>
            </CardHeader>

            <CardContent className="pt-0">
              {inv.personal_note && (
                <div className="bg-muted/50 border-l-2 border-muted-foreground/30 px-3 py-2 rounded-sm mb-3">
                  <p className="text-xs font-medium text-muted-foreground mb-0.5">
                    Personal note
                  </p>
                  <p className="text-sm italic line-clamp-3">{inv.personal_note}</p>
                </div>
              )}

              {(() => {
                const friendly = formatInvitationError(inv.last_error);
                return friendly ? (
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mb-3">
                    <AlertTriangle className="h-3 w-3 text-destructive shrink-0" aria-hidden />
                    <span>{friendly}</span>
                  </p>
                ) : null;
              })()}

              {(inv.status === 'pending' || inv.status === 'bounced') && (() => {
                const failed = previousAttemptFailed(inv);
                const allowResend = canResend(inv);
                const tooltip = !allowResend
                  ? `You can re-send up to ${MAX_RESENDS} times, then again after ${RESEND_COOLDOWN_HOURS}h.`
                  : failed
                    ? 'Try sending the invitation again'
                    : 'Re-send invitation';
                return (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant={failed ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleResend(inv.id)}
                      disabled={!allowResend}
                      title={tooltip}
                      className="gap-1"
                    >
                      <RefreshCw className="h-3 w-3" />
                      {failed ? 'Try again' : 'Resend'}
                      {!failed && (inv.resend_count ?? 0) > 0 && (
                        <span className="text-muted-foreground">
                          ({inv.resend_count}/{MAX_RESENDS})
                        </span>
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRevoke(inv.id)}
                      className="gap-1 text-destructive hover:text-destructive"
                    >
                      <XIcon className="h-3 w-3" />
                      Revoke
                    </Button>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default SentInvitationsList;
